import { IWIZError } from '../../interfaces/iwiz-error';


export interface WizValidator {
  errors: WizValidationError;
  validate: WizValidatorFn;
}

export type WizValidatorFn = (compo?: IWIZError) => WizValidationErrors | null;

export type WizValidationErrors = WizValidationError[];
export interface WizValidationError {
  wizError: string;
  wizErrorParams?: any[];
}

/**
 * Provides a set of built-in validators that can be used by form controls.
 */
export class WizValidators {

  // same as the angular constant email_regexp
  // tslint:disable-next-line: max-line-length
  public static readonly EMAIL_REGEXP: RegExp = /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

  /**
   * @description
   * Validator that performs no operation.
   */
  static nullValidator(compo: IWIZError): WizValidationErrors|null { return null; }

  /**
   * improvement of the angular Validators pattern methode,
   * to allow an errorMessage arg that will be return if the pattern doesn't match
   * @param pattern, the pattern to use
   * @param errorMessage, the message to return
   * @param errorParam, optional, the error param
   */
  static wizPattern(pattern: string|RegExp, errorMessage: string, errorParams?: any[]): WizValidatorFn {
      if (!pattern) { return WizValidators.nullValidator; }
      let regex: RegExp;
      let regexStr: string;
      if (typeof pattern === 'string') {
        regexStr = '';

        if (pattern.charAt(0) !== '^') { regexStr += '^'; }

        regexStr += pattern;

        if (pattern.charAt(pattern.length - 1) !== '$') { regexStr += '$'; }

        regex = new RegExp(regexStr);
      } else {
        regexStr = pattern.toString();
        regex = pattern;
      }
      return (compo: IWIZError): WizValidationErrors | null => {
        if ( compo.getWizValue() === undefined  ||compo.getWizValue() === null || compo.getWizValue().length === 0) {
          return null;  // don't validate empty values to allow optional controls
        }
        const value: string = compo.getWizValue();
        const temp: WizValidationError = {
          wizError: errorMessage,
          wizErrorParams: errorParams
        };
        return regex.test(value) ? null : [temp];
      };
  }

  /**
   * improvement of the angular Validators minlength methode,
   * to allow an errorMessage arg that will be return if the given minlength is not reached
   * @param minLength, the minlength
   * @param errorMessage, the errormessage
   * @param errorParam, optional, the error param
   */
  static wizMinlength(minLength: number, errorMessage: string, errorParams?: any[]): WizValidatorFn {
    return (compo: IWIZError): WizValidationErrors | null => {
      if (compo.getWizValue() === undefined || compo.getWizValue() === null || compo.getWizValue().length === 0) {
        return null;  // don't validate empty values to allow optional controls
      }

      const length: number = compo.getWizValue() ? compo.getWizValue().length : 0;
      const temp: WizValidationError = {
        wizError: errorMessage,
        wizErrorParams: errorParams
      };
      // Controls with NaN values after parsing should be treated as not having a
      // minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
      return  length < minLength ? [temp] : null;
    };
  }

  /**
   * improvement of the angular Validators required methode,
   * to allow an errorMessage arg that will be return if the control is not valid
   * @param errorMessage, the errormessage
   */
  static wizRequired(errorMessage: string): WizValidatorFn {
    return (compo: IWIZError): WizValidationErrors | null => {
      const temp: WizValidationError = {
        wizError: errorMessage
      };

      let res: boolean = (compo.getWizValue() !== null && compo.getWizValue() !== undefined && /([^\s])/.test(compo.getWizValue()));
      if(res) {
        return null;
      } else {
        return [temp];
      }
    };
  }

  /**
   * improvement of the angular Validators required methode,
   * to allow an errorMessage arg that will be return if the control is not valid
   * @param errorMessage, the errormessage
   */
  static wizDropdownRequired(errorMessage: string): WizValidatorFn {
    return (compo: IWIZError): WizValidationErrors | null => {
      const temp: WizValidationError = {
        wizError: errorMessage
      };

      // La drop renvoie un array qui contient la valeur "undefined" quand la valeur est non définie
      if (!!compo.getWizValue() && compo.getWizValue().length === 1 && (compo.getWizValue()[0] === undefined || compo.getWizValue()[0] === null)) {
        return [temp];
      } else if (compo.getWizValue() !== null && compo.getWizValue() !== undefined && compo.getWizValue().length >  0) {
        return null
      }

      return [temp];
    };
  }

  /**
   * improvement of the angular Validators required methode,
   * to allow an errorMessage arg that will be return if the control is not valid
   * Validator specially created for the wiz-checkbox
   * @param errorMessage, the errormessage
   */
  static wizBooleanRequired(errorMessage: string): WizValidatorFn {
    return (compo: IWIZError): WizValidationErrors | null => {
      const temp: WizValidationError = {
        wizError: errorMessage
      };
      return (compo.getWizValue() !== null && compo.getWizValue() !== undefined && compo.getWizValue() !== false) ? null : [temp];
    };
  }

  /**
   * improvement of the angular Validators email methode,
   * to allow an errorMessage arg that will be return if the EMAIL_REGEXP doesn't match with the control value
   * @param errorMessage, the errormessage
   */
  static wizEmail(errorMessage: string): WizValidatorFn {
    return (compo: IWIZError): WizValidationErrors | null => {
        if (!!compo.getWizValue()) {
          const temp: WizValidationError = {
            wizError: errorMessage
          };
  
          return WizValidators.EMAIL_REGEXP.test(compo.getWizValue()) ? null : [temp];
        }
        return null;  // don't validate empty values to allow optional controls
    };
  }

  /**
   * improvement of the angular Validators maxLength methode,
   * to allow an errorMessage arg that will be return if the given maxLength is not exceeded
   * @param maxLength, the allowed maxlength
   * @param errorMessage, the errormessage
   * @param errorParam, optional, the error param
   */
  static wizMaxLength(maxLength: number, errorMessage: string, errorParams?: any[]): WizValidatorFn {
    return (compo: IWIZError): WizValidationErrors | null => {
      // console.log("max length", compo.getWizValue(), maxLength);
      const length: number = compo.getWizValue() ? compo.getWizValue().length : 0;
      const temp: WizValidationError = {
        wizError: errorMessage,
        wizErrorParams: errorParams
      };
      return length > maxLength ? [temp] : null;
    };
  }

  /**
   * @description
   * Validator that requires the control's value to be less than or equal to the provided number.
   * The validator exists only as a function and not as a directive.
   *
   * @usageNotes
   *
   */
  static wizMax(max: number, srcValue: number, errorMessage: string, errorParams?: any[]): WizValidatorFn {
    return (compo: IWIZError): WizValidationErrors | null => {
      if (srcValue === undefined || srcValue == null || max == null) {
        return null;  // don't validate empty values to allow optional controls
      }
      const temp: WizValidationError = {
        wizError: errorMessage,
        wizErrorParams: errorParams
      };
      const result: boolean = srcValue > max;
      return result ? [temp] : null;
    };
  }

  static wizMin(min: number, srcValue: number, errorMessage: string, errorParams?: any[]): WizValidatorFn {
    return (compo: IWIZError): WizValidationErrors | null => {
      if (srcValue === undefined || srcValue == null || min == null) {
        return null;  // don't validate empty values to allow optional controls
      }
      const temp: WizValidationError = {
        wizError: errorMessage,
        wizErrorParams: errorParams
      };
      const result: boolean = srcValue < min;
      return result ? [temp] : null;
    };
  }
}
